import React, { memo, useEffect, useRef, useState } from 'react';
import InboxMessageItem from './InboxMessageItem';
import { useInbox } from '../hooks';
import { Box } from '@bonanza-social/moonlight';
import { FlatList, ViewToken } from 'react-native';
import { useDispatch, useSelector } from '@bonanza-social/toolkit/state';
import {
  getMarketingPreferences,
  selectMarketingPreferences,
  selectMarketingPreferencesDataState,
  updateReadMessagesList,
} from '../state/inbox.slice';
import { InboxMessageResponse, InboxMessageCategory } from '../state';
import InboxEmptyCategory from './InboxEmptyCategory';
import InboxMarketingTurnedOffMessages from './InboxMarketingTurnedOffMessages';
import InboxSkeleton from './InboxSkeleton';

function InboxMessageList({ type }: { type: InboxMessageCategory }) {
  const dispatch = useDispatch();
  const { inbox, inboxDataState } = useInbox(type);

  const marketingPreferences = useSelector(selectMarketingPreferences);
  const marketingPreferencesDataState = useSelector(
    selectMarketingPreferencesDataState,
  );

  const [seenMessages, setSeenMessages] = useState<number[]>([]);

  const handleViewableItemsChanged = useRef(
    ({
      viewableItems,
    }: {
      viewableItems: Array<ViewToken<InboxMessageResponse>>;
    }) => {
      const newIds = viewableItems
        .filter(({ isViewable, item }) => isViewable && !item.isRead)
        .map(({ item }) => item.id);

      if (newIds.length > 0) {
        setSeenMessages((prev) => [...new Set([...prev, ...newIds])]);
      }
    },
  ).current;

  useEffect(() => {
    dispatch(updateReadMessagesList(seenMessages));
  }, [dispatch, seenMessages]);

  useEffect(() => {
    if (type === 'Marketing') {
      dispatch(getMarketingPreferences());
    }
  }, [type, dispatch]);

  if (type === 'Marketing' && marketingPreferencesDataState === 'pending') {
    return <InboxSkeleton />;
  }

  if (type === 'Marketing' && marketingPreferences === false) {
    return <InboxMarketingTurnedOffMessages />;
  }

  return (
    <FlatList
      removeClippedSubviews={false}
      ItemSeparatorComponent={() => (
        <Box
          marginHorizontal="space-600"
          backgroundColor="secondaryBackgroundDefault"
          height="space-025"
        />
      )}
      viewabilityConfig={{ itemVisiblePercentThreshold: 100 }}
      onViewableItemsChanged={handleViewableItemsChanged}
      data={inbox}
      renderItem={({ item }) => <InboxMessageItem {...item} />}
      keyExtractor={(item) => `${item.id}`}
      contentContainerStyle={{
        paddingBottom: 30,
        paddingTop: 30,
      }}
      ListEmptyComponent={
        inboxDataState === 'pending' ? (
          <InboxSkeleton />
        ) : (
          <InboxEmptyCategory type={type} />
        )
      }
    />
  );
}

export default memo(InboxMessageList);
